// features/signup/signupSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { registerUserWithEmailAndPasswordCredentials } from './signup.service';
import { logOut } from '../../../redux/auth/authSlice';
import { UserOnboardingPayload, submitUserOnboardingApi } from '../onboarding/onboarding.service';

export const registerUser = createAsyncThunk(
  'signup/registerUser',
  async (payload: any, { rejectWithValue }) => {
    try {
      const data: { response: any; statusCode: number; message: string } = await registerUserWithEmailAndPasswordCredentials(payload);

      if (!((data as any)?.statusCode === 200 || (data as any)?.statusCode === 201)) {
        return rejectWithValue((data as any)?.message || 'Registration failed');
      }

      if (data.response) {
        localStorage.setItem("user", JSON.stringify(data.response));
      }
      if (document.cookie) {
        try {
          const isCookiesAccepted = localStorage.getItem("accept");
          if (isCookiesAccepted && (window as any).ttq) {
            (window as any).ttq.track("CompleteRegistration");
          }
        } catch (error) {
          console.warn('Tracking error:', error);
        }
      }

      return data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'An unexpected error occurred during registration';
      return rejectWithValue(message);
    }
  }
);

export const submitUserOnboarding = createAsyncThunk(
  'signup/submitUserOnboarding',
  async (payload: UserOnboardingPayload, { rejectWithValue }) => {
    try {
      const response = await submitUserOnboardingApi(payload);

      if (!(response.statusCode === 200 || response.statusCode === 201)) {
        return rejectWithValue(response.message || 'Profile update failed');
      }

      return response;
    } catch (error: any) {
      // Pass full error detail so debug alerts can show the API's actual response body
      const detail = error?.response?.data
        ? JSON.stringify(error.response.data)
        : error?.message || 'An unexpected error occurred during profile update';
      return rejectWithValue(detail);
    }
  }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

type SignupState = {
  message: string | null;
  userData: Awaited<ReturnType<typeof registerUserWithEmailAndPasswordCredentials>> | null;
  loading: boolean;
  error: string | null;
  isSuccess: boolean;
  isRegistered: boolean;
};

const initialState: SignupState = {
  message: null,
  userData: null,
  loading: false,
  error: null,
  isSuccess: false,
  isRegistered: false,
};

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    clearSignupData: (state) => {
      state.userData = null;
      state.error = null;
      state.message = null;
      state.isSuccess = false;
      state.isRegistered = false;
    },
    resetSignupState: () => initialState,
    setSignupMessage: (state, action) => {
      state.message = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(registerUser.pending, state => {
        state.loading = true;
        state.error = null;
        state.isSuccess = false;
        state.message = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isSuccess = true;
        state.isRegistered = true;
        state.userData = action.payload;
        state.message = 'Registration successful!';
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.isSuccess = false;
        state.isRegistered = false;
        state.error = action.payload as string || action.error.message || 'Registration failed';
        state.userData = null;
      })
      .addCase(submitUserOnboarding.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitUserOnboarding.fulfilled, (state, action) => {
        state.loading = false;
        state.message = 'Profile updated successfully!';
        state.error = null;
      })
      .addCase(submitUserOnboarding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || action.error.message || 'Profile update failed';
      })
      .addCase(logOut, () => initialState);
  },
});

export const { clearSignupData, resetSignupState, setSignupMessage } = signupSlice.actions;
export default signupSlice.reducer;
